import { isLoggedInAtom, presentInsuredDetailsAtom, userInfoAtom, userPlanAtom } from 'app/atoms';
import { presentPayWallAtom } from 'app/atoms/ui';
import { InsuredBadge } from 'app/design/icons/insured-badge';
import { Column, Row } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useClickOutside } from 'app/hooks/use-click-outside';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { getI18nLanguage } from 'app/lib/language';
import { safelyOpenURL } from 'app/lib/navigation';
import { preparePolicyUrl } from 'app/lib/text';
import { shouldShowHelpCompensationSubscribe, useMyActivePlans } from 'app/network/plan';
import { useUser } from 'app/network/user';
import { useTrackNewListingSource } from 'app/network/listing';
import { ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView } from 'react-native';
import { navigateToQuickPublishFlow } from '../intro/navigator-helper';
import { useTheme } from 'app/shopify';
import { IconChevronRight, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { HelpRoleToggle } from './help-role-toggle';
import { HelpCompensationSubscribeBanner } from './help-compensation-subscribe-banner';
import { HelpStepRow } from './help-step-row';
import { HelpGuestSignUpCta } from './help-guest-sign-up-cta';

type HelpRole = 'borrower' | 'lender';

interface HelpStepLink {
  labelKey: string;
  onPress: () => void;
}

interface HelpStepConfig {
  icon: React.ReactNode;
  titleKey: string;
  textKey: string;
  links?: HelpStepLink[];
}

export const HelpScreenComponent: NavigationScreen<'help'> = () => {
  const theme = useTheme();
  const navigation = useNavigation<'help'>();
  const { t } = useTranslation(['help', 'contact-us']);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { data: user, isPending: isUserPending } = useUser();
  const trackNewListingSource = useTrackNewListingSource();
  const userInfo = useAtomValue(userInfoAtom);
  const { data: userPlansFromQuery } = useMyActivePlans();
  const userPlansFromAtom = useAtomValue(userPlanAtom);
  const activePlan = userPlansFromQuery?.[0] ?? userPlansFromAtom?.[0];
  const showSubscribeCompensation = shouldShowHelpCompensationSubscribe(user?.insured ?? undefined, activePlan);
  const setInsuredDetails = useSetAtom(presentInsuredDetailsAtom);
  const setPaywallVisible = useSetAtom(presentPayWallAtom);
  const openNewWithGuard = useOpenNewWithGuard();
  const [role, setRole] = useState<HelpRole>('borrower');
  const i18nLanguage = getI18nLanguage();

  const close = () => {
    navigation.goBack();
  };

  const goToExplore = () => {
    navigation.navigate(
      'main',
      {
        screen: 'billboard',
        params: { screen: 'all-listings', params: { type: ListingType.offer } },
      },
      { pop: true },
    );
  };

  const goToNewNeed = () => {
    openNewWithGuard(navigation, { defaultValues: { type: ListingType.request }, newListingSource: 'help' });
  };

  const goToNewOffer = () => {
    openNewWithGuard(navigation, { defaultValues: { type: ListingType.offer }, newListingSource: 'help' });
  };

  const goToQuickOffers = () => {
    trackNewListingSource('help');
    navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
  };

  const goToSignUp = () => {
    navigation.navigate('onboarding', { screen: 'contact-information' });
  };

  const visitHelpCenter = () => {
    void safelyOpenURL(preparePolicyUrl(t('faqLink', { ns: 'contact-us' }), i18nLanguage, user));
  };

  const showCompensationDetails = () => {
    if (userInfo) {
      setInsuredDetails(userInfo);
    }
  };

  const goToSubscribe = () => {
    close();
    setTimeout(() => setPaywallVisible(true), 10);
  };

  const viewRef = React.useRef(null);

  useClickOutside([viewRef], (clickedOutside) => {
    if (clickedOutside) {
      navigation.goBack();
    }
  });

  const borrowerSteps: HelpStepConfig[] = [
    {
      icon: <Text>🔍</Text>,
      titleKey: 'borrower.steps.catalog.title',
      textKey: 'borrower.steps.catalog.text',
      links: [
        { labelKey: 'borrower.steps.catalog.exploreLink', onPress: goToExplore },
        { labelKey: 'borrower.steps.catalog.needLink', onPress: goToNewNeed },
      ],
    },
    {
      icon: <Text>🗓️</Text>,
      titleKey: 'borrower.steps.meeting.title',
      textKey: 'borrower.steps.meeting.text',
    },
    {
      icon: <Text>🫳</Text>,
      titleKey: 'borrower.steps.pickup.title',
      textKey: 'borrower.steps.pickup.text',
    },
    {
      icon: <Text>✨</Text>,
      titleKey: 'borrower.steps.return.title',
      textKey: 'borrower.steps.return.text',
    },
  ];

  const lenderSteps: HelpStepConfig[] = [
    {
      icon: <Text>📸</Text>,
      titleKey: 'lender.steps.photo.title',
      textKey: 'lender.steps.photo.text',
    },
    {
      icon: <Text>✍️</Text>,
      titleKey: 'lender.steps.publish.title',
      textKey: 'lender.steps.publish.text',
      links: [
        { labelKey: 'lender.steps.publish.offerLink', onPress: goToNewOffer },
        { labelKey: 'lender.steps.publish.suggestedLink', onPress: goToQuickOffers },
      ],
    },
    {
      icon: <Text>🫴</Text>,
      titleKey: 'lender.steps.share.title',
      textKey: 'lender.steps.share.text',
    },
    {
      icon: <Text>✨</Text>,
      titleKey: 'lender.steps.return.title',
      textKey: 'lender.steps.return.text',
    },
  ];

  const steps = role === 'borrower' ? borrowerSteps : lenderSteps;
  const pagePadding = usePagePadding(true);

  return (
    <Page
      isModal
      paddingStyle={{
        ...pagePadding,
        paddingLeft: Math.max(pagePadding.paddingLeft, theme.spacing['spacing.general.x6']),
        paddingRight: Math.max(pagePadding.paddingRight, theme.spacing['spacing.general.x6']),
      }}
    >
      <Column gap="spacing.general.x8" flex={1} ref={viewRef}>
        <Row alignItems="center" gap="spacing.general.x4" width="100%">
          <Text flex={1} flexShrink={1} fontSize={22} fontWeight="700" style={{ minWidth: 0 }} variant="bodyBase">
            {t('title')}
          </Text>
          <Pressable
            accessibilityLabel={t('close', { ns: 'accessibility' })}
            accessibilityRole="button"
            hitSlop={12}
            onPress={close}
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          </Pressable>
        </Row>

        <HelpRoleToggle onChange={setRole} role={role} />

        <ScrollView
          contentContainerStyle={{ gap: theme.spacing['spacing.general.x8'] }}
          showsVerticalScrollIndicator={false}
        >
          <Column
            gap="spacing.general.x8"
            style={{
              borderWidth: 1,
              borderColor: theme.colors['surface.inverse.tertiary-neutral'],
              borderRadius: theme.borderRadii['radius.lg'],
              padding: theme.spacing['spacing.general.x8'],
            }}
          >
            {steps.map((step) => (
              <HelpStepRow key={step.titleKey} step={step} />
            ))}

            {!isWhiteLabel(WhiteLabel.HauteGaspesie) &&
              (showSubscribeCompensation ? (
                <HelpCompensationSubscribeBanner
                  actions={
                    isLoggedIn ? { onLearnMore: showCompensationDetails, onSubscribe: goToSubscribe } : undefined
                  }
                />
              ) : (
                !isWhiteLabel(WhiteLabel.Mutuo) && (
                  <Pressable onPress={showCompensationDetails}>
                    <Row
                      alignItems="center"
                      gap="spacing.general.x4"
                      style={{
                        backgroundColor: theme.colors['surface.brand.subtle'],
                        borderRadius: theme.borderRadii['radius.lg'],
                        padding: theme.spacing['spacing.general.x4'],
                      }}
                    >
                      <InsuredBadge />
                      <Column flex={1} gap="spacing.general.x1">
                        <Text fontWeight="700" variant="bodyBase">
                          {t('compensation.title')}
                        </Text>
                        <Text fontSize={14} variant="bodyBase">
                          {t('compensation.text')}
                        </Text>
                      </Column>
                      <IconChevronRight
                        strokeWidth={ICON_STROKE_WIDTH}
                        size={24}
                        color={theme.colors['text.primary']}
                      />
                    </Row>
                  </Pressable>
                )
              ))}
          </Column>

          {!isLoggedIn ? <HelpGuestSignUpCta onPress={goToSignUp} /> : null}
        </ScrollView>

        <Column alignItems="center" gap="spacing.general.x2">
          <Text color="text.secondary" textAlign="center" variant="bodyBase">
            {t('assistanceQuestion')}
          </Text>
          <Pressable onPress={visitHelpCenter}>
            <Text color="text.emphasized" fontWeight="700" fontSize={16}>
              {t('visitHelpCenter')} →
            </Text>
          </Pressable>
        </Column>
      </Column>
    </Page>
  );
};
